import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import type { GlobalCategory } from '@/types/category.types';
import type { CostCategoryType } from '@/types/budget.types';
import {
  MOCK_NOMINAL_CODES,
  MOCK_SALES_NOMINAL_CODES,
  getCategoryInstanceMapping,
  saveCategoryInstanceMapping,
  type CategoryInstanceMapping,
} from '@/lib/cost-category-mapping-storage';
import {
  NominalCodePicker,
  COST_RECENTLY_USED,
  SALES_RECENTLY_USED,
} from '@/components/settings/NominalCodePicker';

const COST_CATEGORY_TYPES: { value: CostCategoryType; label: string }[] = [
  { value: 'labour',        label: 'Labour' },
  { value: 'expenses',      label: 'Expenses' },
  { value: 'subcontractors',label: 'Subcontractors' },
  { value: 'materials',     label: 'Materials' },
  { value: 'equipment',     label: 'Equipment' },
  { value: 'overhead',      label: 'Overhead' },
  { value: 'contingency',   label: 'Contingency' },
];

interface CategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category?: GlobalCategory | null;
  onSave: (data: {
    name: string;
    code?: string;
    type: CostCategoryType;
    description?: string;
    isDefault: boolean;
  }) => Promise<void>;
}

export function CategoryFormDialog({
  open,
  onOpenChange,
  category,
  onSave,
}: CategoryFormDialogProps) {
  const isEditing = !!category;

  // ── Details tab state ──────────────────────────────────────────────────────
  const [name,        setName]        = useState('');
  const [code,        setCode]        = useState('');
  const [type,        setType]        = useState<CostCategoryType>('labour');
  const [description, setDescription] = useState('');
  const [isDefault,   setIsDefault]   = useState(false);
  const [isSaving,    setIsSaving]    = useState(false);
  const [errors,      setErrors]      = useState<Record<string, string>>({});

  // ── Nominal Codes tab state ────────────────────────────────────────────────
  const [costCodes,  setCostCodes]  = useState<string[]>([]);
  const [salesCodes, setSalesCodes] = useState<string[]>([]);

  // ── Populate on open ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    if (category) {
      setName(category.name);
      setCode(category.code || '');
      setType(category.type);
      setDescription(category.description || '');
      setIsDefault(category.isDefault);

      // Load any previously saved nominal code mappings for this category
      const saved = getCategoryInstanceMapping(String(category.id));
      setCostCodes(saved.costCodes);
      setSalesCodes(saved.salesCodes);
    } else {
      setName('');
      setCode('');
      setType('labour');
      setDescription('');
      setIsDefault(false);
      setCostCodes([]);
      setSalesCodes([]);
    }
    setErrors({});
  }, [open, category]);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Name is required';
    if (!type)        newErrors.type = 'Type is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      await onSave({
        name: name.trim(),
        code: code.trim() || undefined,
        type,
        description: description.trim() || undefined,
        isDefault,
      });

      // If editing, persist nominal code assignments immediately
      if (category) {
        const mapping: CategoryInstanceMapping = { costCodes, salesCodes };
        saveCategoryInstanceMapping(String(category.id), mapping);
      }
      // For new categories we don't have an id yet — the parent will need to
      // call saveCategoryInstanceMapping once the server returns the new id.
      // We expose the pending codes via a custom event so the parent can pick them up.
      if (!category) {
        (window as unknown as Record<string, unknown>).__pendingNominalCodes = { costCodes, salesCodes };
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save category:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Sales categories — only Labour, Expenses, Subcontractors, Materials, Equipment ──
  const showSalesTab = ['labour', 'expenses', 'subcontractors', 'materials', 'equipment'].includes(type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Category' : 'Add Category'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the details of this global budget category and assign nominal codes.'
              : 'Create a new global budget category available across all projects.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
            <TabsTrigger value="nominal-codes" className="flex-1">
              Nominal Codes
              {(costCodes.length > 0 || salesCodes.length > 0) && (
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold w-5 h-5">
                  {costCodes.length + salesCodes.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── Details tab ── */}
          <TabsContent value="details" className="space-y-4 pt-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="category-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="category-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Labour, Equipment, Travel"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            {/* Code */}
            <div className="space-y-2">
              <Label htmlFor="category-code">Code</Label>
              <Input
                id="category-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. LAB-001"
              />
            </div>

            {/* Type */}
            <div className="space-y-2">
              <Label htmlFor="category-type">
                Type <span className="text-destructive">*</span>
              </Label>
              <Select value={type} onValueChange={(val) => setType(val as CostCategoryType)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a type" />
                </SelectTrigger>
                <SelectContent>
                  {COST_CATEGORY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.type && (
                <p className="text-sm text-destructive">{errors.type}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="category-description">Description</Label>
              <Textarea
                id="category-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description of the category"
                rows={3}
              />
            </div>

            {/* Is Default */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="category-default"
                checked={isDefault}
                onCheckedChange={(checked) => setIsDefault(checked === true)}
              />
              <Label htmlFor="category-default" className="font-normal">
                Default category (pre-populated in new projects)
              </Label>
            </div>
          </TabsContent>

          {/* ── Nominal Codes tab ── */}
          <TabsContent value="nominal-codes" className="pt-4 space-y-6">

            {/* Cost nominal codes */}
            <div className="space-y-3">
              <div>
                <Label>Cost Nominal Codes</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Assign expense or purchase nominal codes for cost tracking on this category.
                </p>
              </div>
              <NominalCodePicker
                allCodes={MOCK_NOMINAL_CODES}
                selected={costCodes}
                onValueChange={setCostCodes}
                recentlyUsedCodes={COST_RECENTLY_USED}
                placeholder="Assign cost nominal codes…"
                dialogTitle="Select Cost Nominal Code"
              />
            </div>

            <Separator />

            {/* Sales nominal codes — only relevant for billable category types */}
            <div className="space-y-3">
              <div>
                <Label>
                  Sales Nominal Codes
                  {!showSalesTab && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      (not applicable for {type} categories)
                    </span>
                  )}
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Assign revenue nominal codes for T&amp;M billing on this category.
                </p>
              </div>
              {showSalesTab ? (
                <NominalCodePicker
                  allCodes={MOCK_SALES_NOMINAL_CODES}
                  selected={salesCodes}
                  onValueChange={setSalesCodes}
                  recentlyUsedCodes={SALES_RECENTLY_USED}
                  placeholder="Assign sales nominal codes…"
                  dialogTitle="Select Sales Nominal Code"
                />
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  Overhead and Contingency categories are internal cost categories and do not
                  have sales nominal code mappings.
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : isEditing ? (
              'Update Category'
            ) : (
              'Add Category'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
